// src/pages/ProfilePage.js
import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../contextStore/AuthContext";
import ProfileComponent from "../Components/Profile/Profile";
import "../Components/Profile/Profile.css";
import client from "../api/client";

/**
 * Single profile page (replaces the old Pages/Profile folder).
 * It fetches /api/profile and passes user + listings down to the Profile component.
 */
export default function ProfilePage() {
  const { token, setUser, initialized } = useContext(AuthContext);
  const [data, setData] = useState({ user: null, listings: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!initialized) return;

    if (!token) {
      navigate("/login");
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    client
      .get("/api/profile", {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        responseType: "text", // kept as text to preserve current defensive parsing
      })
      .then((res) => {
        if (cancelled) return;

        const contentType = (res.headers && res.headers["content-type"]) || "";
        const text = typeof res.data === "string" ? res.data : JSON.stringify(res.data);

        if (!contentType.includes("application/json")) {
          console.warn("ProfilePage: non-JSON response from /api/profile", {
            status: res.status,
            contentType,
            bodySnippet: text.slice(0, 1000),
          });
          throw new Error(`Server returned non-JSON (${res.status})`);
        }

        let json;
        try {
          json = typeof res.data === "string" ? JSON.parse(res.data) : res.data;
        } catch (e) {
          console.warn("ProfilePage: failed to parse JSON from /api/profile", e);
          throw new Error("Invalid JSON from server");
        }

        setData({ user: json.user || null, listings: json.listings || [] });

        try {
          if (json.user && typeof setUser === "function") {
            setUser(json.user, token);
          }
        } catch (e) {
          // ignore
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("profile fetch error:", err);

        const status = err?.response?.status;
        const bodySnippet = err?.response?.data
          ? (typeof err.response.data === "string" ? err.response.data.slice(0, 800) : JSON.stringify(err.response.data).slice(0, 800))
          : "";

        if (status === 401) {
          try {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            if (typeof setUser === "function") setUser(null, null);
          } catch (e) {}
          navigate("/login");
          return;
        }

        setError({
          message: err.message || "Failed to fetch profile",
          status,
          bodySnippet,
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, initialized, navigate, setUser]);

  const handleLogout = () => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      if (typeof setUser === "function") setUser(null, null);
    } catch (e) {}
    navigate("/login");
  };

  // NEW: navigate to edit page for a listing
  const handleEdit = (listingId) => {
    if (!listingId) return;
    navigate(`/edit/${listingId}`);
  };

  if (!initialized) return <div style={{ padding: 40 }}>Loading profile…</div>;
  if (loading) return <div style={{ padding: 40 }}>Loading profile…</div>;

  if (error) {
    return (
      <div style={{ padding: 40 }}>
        <h3>Could not load profile</h3>
        <p>{error.message}</p>
        {error.status && <p>Status: {error.status}</p>}
        {error.bodySnippet && (
          <>
            <p>Response snippet:</p>
            <pre style={{ whiteSpace: "pre-wrap", maxHeight: 240, overflow: "auto" }}>
              {error.bodySnippet}
            </pre>
          </>
        )}
        <button onClick={() => { setLoading(true); setError(null); window.location.reload(); }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <ProfileComponent
      user={data.user || {}}
      listings={data.listings || []}
      onLogout={handleLogout}
      onEdit={handleEdit}         // <-- pass handler to child
    />
  );
}
